import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ConfirmationModal } from '../../components/ConfirmationModal'
import { CustomModal } from '../../components/CustomModal'
import { MailingTermsModalContent } from '../../components/MailingDownloadButton/MailingTermsModalContent'
import { ErrorState, formatDayMonth, ReportLoading, salesReportIcons } from '../../components/SalesReport'
import { AbandonedCartRequest, AbandonedCartResponse, useAbandonedCartExport, useAbandonedCartReport } from '../../hooks/abandoned-cart'
import { useAuth, useGuard, useHasAccess } from '../../hooks/auth'
import { useEvent } from '../../hooks/events'
import { exportErrorMessage, SalesReportExportFormat } from '../../hooks/sales-report'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import * as Page from '../SalesReport/styles'
import { defaultPeriod, periodError } from './period'
import { LegacyAbandonedCart } from './Legacy'
import { ReportContent } from './ReportContent'
import * as S from './styles'

export const ABANDONED_CART_PERMISSION = 'DP9'
export const NEW_ABANDONED_CART_PERMISSION = 'DP28'

const FORMAT_LABELS: Record<SalesReportExportFormat, string> = { csv: 'CSV', xlsx: 'XLSX' }

const noop = (): void => {}

const reportErrorMessage = (error: any): string | undefined =>
  error?.response?.data?.errorCode === 'REPORT_TOO_LARGE' ? exportErrorMessage('REPORT_TOO_LARGE') : undefined

export function AbandonedCart(): ReactElement {
  useGuard([ABANDONED_CART_PERMISSION])
  const { id } = useParams()
  const eventId = Number(id)
  const navigate = useNavigate()
  const { authHookWithEvent } = useAuth()
  const { event } = useEvent(eventId)
  const mobile = useMediaQuery('(max-width: 767px)')
  const [draft, setDraft] = useState<AbandonedCartRequest>(() => defaultPeriod())
  const [period, setPeriod] = useState<AbandonedCartRequest>(draft)
  const draftError = periodError(draft)
  const request = useMemo(() => (periodError(period) === null ? period : null), [period])
  const { report, error, isLoading, isValidating, refresh } = useAbandonedCartReport(eventId, request)
  const exporter = useAbandonedCartExport(eventId)

  const [pendingFormat, setPendingFormat] = useState<SalesReportExportFormat | null>(null)
  const [termsOpen, setTermsOpen] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [codeOpen, setCodeOpen] = useState(false)
  const code = useRef('')
  const submitted = useRef(false)
  const requestRef = useRef(request)
  const formatRef = useRef(pendingFormat)
  requestRef.current = request
  formatRef.current = pendingFormat
  const { start } = exporter

  useEffect(() => {
    if (!LOCAL_EXECUTION && id) authHookWithEvent(id, navigate)
  }, [id, authHookWithEvent, navigate])

  const exporting = exporter.state.status === 'running' ? exporter.state.format : null
  const canExport = request !== null && exporting === null && (report?.people.listed ?? 0) > 0
  const refreshing = isValidating && !isLoading
  const overline = event ? [formatDayMonth(event.data), event.nome].filter(Boolean).join(' — ') : ''

  const askExport = (format: SalesReportExportFormat): void => {
    setPendingFormat(format)
    setTermsAccepted(false)
    setTermsOpen(true)
  }

  const confirmTerms = (): void => {
    if (!termsAccepted) return
    setTermsOpen(false)
    code.current = ''
    submitted.current = false
    setCodeOpen(true)
  }

  const setCode = useCallback((value: string | ((previous: string) => string)): void => {
    code.current = typeof value === 'function' ? value(code.current) : value
  }, [])

  const submitExport = useCallback(async (): Promise<{ data: { success: boolean }, error: null }> => {
    const current = requestRef.current
    const format = formatRef.current
    if (!submitted.current && current !== null && format !== null) {
      submitted.current = true
      void start(format, { ...current, termsAccepted: true, code2FA: code.current })
    }
    return { data: { success: true }, error: null }
  }, [start])

  const closeCode = useCallback((): void => setCodeOpen(false), [])

  let body: ReactNode = null
  if (request !== null) {
    if (isLoading || (Boolean(error) && isValidating)) body = <ReportLoading />
    else if (error) body = <ErrorState message={reportErrorMessage(error)} onRetry={refresh} />
    else if (report === undefined) body = <ReportLoading />
    else body = <ReportContent report={report as AbandonedCartResponse} mobile={mobile} />
  }

  return (
    <Page.Page>
      <Page.Header>
        <Page.Heading>
          <Page.Overline>{overline}</Page.Overline>
          <Page.Title>Carrinho abandonado</Page.Title>
          <Page.Description>
            Veja quem iniciou a compra e não concluiu, em que etapa parou e por quê. Quem comprou depois sai da lista.
          </Page.Description>
        </Page.Heading>
        <Page.Actions>
          <Page.ActionButton type="button" disabled={request === null || refreshing} aria-busy={refreshing || undefined} onClick={refresh}>
            {refreshing ? 'Atualizando…' : '↻ Atualizar'}
          </Page.ActionButton>
          <Page.ActionButton
            type="button"
            disabled={!canExport}
            aria-label="Exportar CSV"
            aria-busy={exporting === 'csv' || undefined}
            onClick={() => askExport('csv')}
          >
            <img src={salesReportIcons.download} alt="" width={16} height={16} />
            <Page.DesktopOnly>Exportar CSV</Page.DesktopOnly>
            <Page.MobileOnly>CSV</Page.MobileOnly>
          </Page.ActionButton>
          <Page.ActionButton
            type="button"
            $primary
            disabled={!canExport}
            aria-busy={exporting === 'xlsx' || undefined}
            onClick={() => askExport('xlsx')}
          >
            <img src={salesReportIcons.downloadLight} alt="" width={16} height={16} />
            Exportar XLSX
          </Page.ActionButton>
        </Page.Actions>
      </Page.Header>
      <Page.ExportStatus role="status" aria-live="polite">
        {exporting && `Gerando arquivo ${FORMAT_LABELS[exporting]}…`}
      </Page.ExportStatus>
      {exporter.state.status === 'error' && <Page.ExportError role="alert">{exporter.state.message}</Page.ExportError>}

      <S.Period
        as="form"
        onSubmit={(submitted: { preventDefault: () => void }) => {
          submitted.preventDefault()
          if (draftError === null) setPeriod(draft)
        }}
      >
        <S.Field>
          <span>De</span>
          <input type="date" value={draft.from} onChange={(changed) => setDraft({ ...draft, from: changed.target.value })} />
        </S.Field>
        <S.Field>
          <span>Até</span>
          <input type="date" value={draft.to} onChange={(changed) => setDraft({ ...draft, to: changed.target.value })} />
        </S.Field>
        <Page.ActionButton type="submit" disabled={draftError !== null}>
          Aplicar
        </Page.ActionButton>
      </S.Period>
      {draftError !== null && <S.FieldError role="alert">{draftError}</S.FieldError>}

      {body}

      <CustomModal isOpen={termsOpen} onRequestClose={() => setTermsOpen(false)}>
        <MailingTermsModalContent
          accepted={termsAccepted}
          setAccepted={setTermsAccepted}
          onCancel={() => setTermsOpen(false)}
          onConfirm={confirmTerms}
        />
      </CustomModal>

      {codeOpen && (
        <ConfirmationModal
          isOpen
          setIsOpen={setCodeOpen}
          setCode={setCode}
          setIsSuccess={noop}
          setConfirmationMsg={noop}
          setIsConfirmationOpen={noop}
          submitResponse={submitExport}
          submitLabel="Confirmar exportação"
          skip2faPreValidation
          onRequestClose={closeCode}
        />
      )}
    </Page.Page>
  )
}

export function AbandonedCartRoute(): ReactElement {
  const { id } = useParams()
  const newReport = useHasAccess(NEW_ABANDONED_CART_PERMISSION)
  if (newReport) return <AbandonedCart key={id} />
  return <LegacyAbandonedCart />
}
